import cn from "classnames";
import React from "react";
import LazyImage from "web/react/components/lazy-load-image/lazy-load-image";
import { isExperimentRunning } from "web/react/hooks/use-web-to-app-campaign/use-web-to-app-campaign";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import storage from "web/script/utils/storage";
import { useProductCardContext } from "./product-card";
import styles from "./product-card-image.css";
import ProductCardLink from "./product-card-link";
import ProductCardLinkKevelEnabled from "./product-card-link-kevel-enabled";

interface ProductCardImageProps {
    width: number;
    height: number;
    children?: React.ReactNode;
    feedType?: string;
    lazyLoad?: boolean;
    analyticsEventLabel?: string;
}

function ProductCardImageWrapper({
    width,
    height,
    children,
    feedType,
    lazyLoad = true,
}: ProductCardImageProps): React.ReactElement {
    return (
        <div className={styles.imageWrapper}>
            {children}
            <ProductCardImage
                width={width}
                height={height}
                lazyLoad={lazyLoad}
                feedType={feedType}
            />
        </div>
    );
}

export function ProductCardImage({
    width,
    height,
    lazyLoad = true,
    feedType,
    analyticsEventLabel,
}: ProductCardImageProps): React.ReactElement {
    const {
        is_affiliate: isAffiliate,
        lead_enabled: leadEnabled,
        use_lazy_images: useLazyImages,
        image_url: imageURL,
        image_alt_text: imageAltText,
        context_type: contextType,
        in_stock: inStock,
    } = useProductCardContext();

    const displayedImage = imageURL || "";

    function handleClick(): void {
        analyticsEventLabel
            ? analytics.event("product_card", "image", analyticsEventLabel)
            : analytics.event("product_card", "image");
    }

    // We want to disable leads on related products if the continue option has been clicked this session
    const stopLeadsOnReleatedProducts =
        storage.get("blockHasBeenDismissed", false, true) &&
        contextType === "related-product" &&
        environment.getFeature("mg_remove_leads_on_related_products_pdp");

    const shouldRenderLeadLinkInImage = isAffiliate && leadEnabled && !stopLeadsOnReleatedProducts;

    if (!imageURL) {
        return (
            <div style={{ position: "relative", width, height }}>
                <ProductCardLink
                    className={styles.imageLink}
                    reason="image"
                    predicate={shouldRenderLeadLinkInImage}
                    onClick={handleClick}
                    feedType={feedType}
                />
            </div>
        );
    }
    /* ORG-2888 Creating Similar in stock button for OOS products */
    const enableSimilarItems = environment.getFeature("seo_enable_similar_items_oos_feeds");

    const productCardImageComponent = (
        <>
            {useLazyImages && lazyLoad ? (
                <LazyImage
                    src={displayedImage}
                    alt={imageAltText}
                    width={width}
                    height={height}
                    placeholder={`data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}"3E%3C/svg%3E`}
                    className={cn(styles.image, {
                        [styles.fadeOutImage]: enableSimilarItems && !inStock,
                    })}
                    data-pin-no-hover="true"
                />
            ) : (
                <img
                    width={width}
                    height={height}
                    src={displayedImage}
                    alt={imageAltText}
                    className={cn(styles.image, {
                        [styles.fadeOutImage]: enableSimilarItems && !inStock,
                    })}
                    data-pin-no-hover="true"
                />
            )}
        </>
    );

    if (environment.getFeature("mg_kevel_campaigns") && !isExperimentRunning()) {
        return (
            <ProductCardLinkKevelEnabled
                reason="image"
                predicate={shouldRenderLeadLinkInImage}
                onClick={handleClick}
                feedType={feedType}
            >
                {productCardImageComponent}
            </ProductCardLinkKevelEnabled>
        );
    } else {
        return (
            <ProductCardLink
                reason="image"
                predicate={shouldRenderLeadLinkInImage}
                onClick={handleClick}
                feedType={feedType}
            >
                {productCardImageComponent}
            </ProductCardLink>
        );
    }
}

export default ProductCardImageWrapper;
